import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { filter, map, Observable } from "rxjs";

import {
    generateCountriesFromShops,
    navigateToCityPage,
    navigateToCountryPage,
    openCurrentCountryContinentAndCloseOthers,
    setContinents,
    setCurrentCity,
    setCurrentCountry,
    toggleContinentAndCloseOthers,
    updateShopLocator,
} from "./shop-locations.actions";
import {
    selectCities,
    selectContinents,
    selectCountries,
    selectCurrentCity,
    selectCurrentCountry,
    selectCurrentLocation,
    selectHasCities,
    selectHasCountries,
    selectMode,
} from "./shop-locations.selectors";
import {
    CityEntry,
    ContinentEntry,
    CountryEntry,
    CurrentShopLocation,
    ShopLocationsCountryMode,
    ShopLocationsDefaultMode,
    ShopLocationsState,
} from "./shop-locations.state";

@Injectable()
export class ShopLocationsFacade {
    public hasCountries$ = this.store.select(selectHasCountries);

    public hasCities$ = this.store.select(selectHasCities);

    public cities$ = this.store.select(selectCities);

    public countries$ = this.store.select(selectCountries);

    public continents$ = this.store.select(selectContinents);

    public mode$ = this.store.select(selectMode);

    public currentCountry$: Observable<CountryEntry | undefined> =
        this.store.select(selectCurrentCountry);

    public currentCity$: Observable<CityEntry | undefined> =
        this.store.select(selectCurrentCity);

    public currentLocation$: Observable<CurrentShopLocation> = this.store
        .select(selectCurrentLocation)
        .pipe(
            filter(
                (currentLocation) =>
                    currentLocation.pageMode !== "uninitialized",
            ),
            map((currentLocation) => currentLocation as CurrentShopLocation),
        );

    constructor(private store: Store<ShopLocationsState>) {}

    /**
     * Reset the Shop Locator data - except for the Shops list.
     */
    public generateCountriesFromShops(): void {
        this.store.dispatch(generateCountriesFromShops());
    }

    public setContinents(continents: ContinentEntry[]) {
        this.store.dispatch(setContinents({ continents }));
    }

    public setCurrentCountry(
        countryCode: string,
        pageMode: ShopLocationsCountryMode | ShopLocationsDefaultMode,
    ): void {
        this.store.dispatch(setCurrentCountry({ countryCode, pageMode }));
    }

    public setCurrentCity(city: CityEntry): void {
        this.store.dispatch(setCurrentCity({ city }));
    }

    public updateShopLocator(): void {
        this.store.dispatch(updateShopLocator());
    }

    public navigateToCountryPage(country: CountryEntry): void {
        this.store.dispatch(navigateToCountryPage({ country }));
    }

    public navigateToCityPage(city: CityEntry): void {
        this.store.dispatch(navigateToCityPage({ city }));
    }

    public toggleContinentAndCloseOthers(continentName: string) {
        this.store.dispatch(toggleContinentAndCloseOthers({ continentName }));
    }

    public openCurrentCountryContinentAndCloseOthers(continentName: string) {
        this.store.dispatch(
            openCurrentCountryContinentAndCloseOthers({ continentName }),
        );
    }
}
